import { Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { ModalController } from '@ionic/angular/standalone';
import { Observable, from, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { EulaService } from '../services/eula.service';
import { EulaModalComponent } from '../components/eula-modal/eula-modal.component';
import { LoggerService } from '../services/logger.service';

@Injectable({
  providedIn: 'root'
})
export class EulaGuard implements CanActivate {
  constructor(
    private eulaService: EulaService,
    private modalController: ModalController,
    private router: Router,
    private logger: LoggerService
  ) {}

  canActivate(): Observable<boolean> {
    this.logger.debug('🔒 EULA Guard - Checking EULA status...');
    return this.eulaService.checkEULAStatus().pipe(
      tap(status => {
        this.logger.debug('🔒 EULA Guard - Status received:', status);
      }),
      switchMap(status => {
        if (status.hasAcceptedEULA) {
          // User has already accepted EULA, allow access
          this.logger.debug('🔒 EULA Guard - User has accepted EULA, allowing access');
          return of(true);
        } else {
          // User hasn't accepted EULA, show modal
          this.logger.debug('🔒 EULA Guard - User has not accepted EULA, showing modal');
          return this.showEULAModal();
        }
      }),
      catchError(error => {
        this.logger.error('🔒 EULA Guard - Error checking EULA status:', error);
        // On error, allow access to prevent blocking the app
        return of(true);
      })
    );
  }

  private showEULAModal(): Observable<boolean> {
    this.logger.debug('🔒 EULA Guard - Showing EULA modal...');
    return from(this.presentEULAModal()).pipe(
      switchMap(result => {
        this.logger.debug('🔒 EULA Guard - Modal result:', result);
        if (result.accepted) {
          // User accepted EULA, allow access
          this.logger.debug('🔒 EULA Guard - User accepted EULA, allowing access');
          return of(true);
        } else {
          // User declined EULA, redirect to logout
          this.logger.debug('🔒 EULA Guard - User declined EULA, redirecting to login');
          this.router.navigate(['/login']);
          return of(false);
        }
      }),
      catchError(error => {
        this.logger.error('🔒 EULA Guard - Error showing EULA modal:', error);
        // On error, allow access to prevent blocking the app
        return of(true);
      })
    );
  }

  private async presentEULAModal(): Promise<{ accepted: boolean }> {
    const modal = await this.modalController.create({
      component: EulaModalComponent,
      componentProps: {},
      backdropDismiss: false,
      keyboardClose: false,
      cssClass: 'eula-modal'
    });

    await modal.present();
    const { data } = await modal.onWillDismiss();
    return data || { accepted: false };
  }
} 